import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl, slugify } from "../../lib/admin";

const empty = {
  name: "",
  slug: "",
  logo: "",
  primary_color: "#E11D48",
  secondary_color: "#FAFAFA",
  banner_image: "",
  description: "",
  domain: "",
  active: true,
};

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/brands").then((r) => setBrands(r.data));
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing("new");
    setForm(empty);
  };
  const openEdit = (b) => {
    setEditing(b.id);
    setForm({ ...empty, ...b });
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, slug: form.slug || slugify(form.name) };
      if (editing === "new") {
        await api.post("/brands", payload);
        toast.success("Brand created");
      } else {
        await api.put(`/brands/${editing}`, payload);
        toast.success("Brand updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?`)) return;
    await api.delete(`/brands/${b.id}`);
    toast.success("Deleted");
    load();
  };

  const uploadFile = async (field, file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm({ ...form, [field]: url });
  };

  return (
    <div data-testid="admin-brands-page">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Catalog</div>
          <h1 className="font-display text-4xl tracking-tighter">Brands</h1>
        </div>
        <button
          onClick={openCreate}
          className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
          data-testid="brand-create-btn"
        >
          <Plus size={14} /> New Brand
        </button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A]" data-testid="brands-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3 font-normal">Logo</th>
              <th className="px-4 py-3 font-normal">Name</th>
              <th className="px-4 py-3 font-normal">Slug</th>
              <th className="px-4 py-3 font-normal">Domain</th>
              <th className="px-4 py-3 font-normal">Color</th>
              <th className="px-4 py-3 font-normal">Active</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {brands.map((b) => (
              <tr key={b.id} className="border-b border-white/5" data-testid={`brand-row-${b.slug}`}>
                <td className="px-4 py-3">
                  {b.logo ? <img src={b.logo} alt="" className="h-8 w-auto object-contain" /> : "—"}
                </td>
                <td className="px-4 py-3 font-medium">{b.name}</td>
                <td className="px-4 py-3 text-zinc-400">{b.slug}</td>
                <td className="px-4 py-3 text-zinc-400">{b.domain || "—"}</td>
                <td className="px-4 py-3">
                  <span
                    className="inline-block w-6 h-6 border border-white/20"
                    style={{ background: b.primary_color }}
                  />
                </td>
                <td className="px-4 py-3">{b.active ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openEdit(b)}
                    className="text-zinc-400 hover:text-white p-2"
                    data-testid={`brand-edit-${b.slug}`}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => remove(b)}
                    className="text-zinc-400 hover:text-red-400 p-2"
                    data-testid={`brand-delete-${b.slug}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {brands.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                  No brands yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          onClick={close}
          data-testid="brand-form-modal"
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 p-6 my-8"
          >
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl">{editing === "new" ? "New Brand" : "Edit Brand"}</div>
              <button type="button" onClick={close} className="text-zinc-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
                  className="adm-input"
                  data-testid="brand-form-name"
                />
              </Field>
              <Field label="Slug">
                <input
                  required
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  className="adm-input"
                  data-testid="brand-form-slug"
                />
              </Field>
              <Field label="Domain (e.g. astrhori.com.tr)">
                <input
                  value={form.domain}
                  onChange={(e) => setForm({ ...form, domain: e.target.value.toLowerCase() })}
                  className="adm-input"
                  data-testid="brand-form-domain"
                />
              </Field>
              <Field label="Active">
                <select
                  value={form.active ? "1" : "0"}
                  onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}
                  className="adm-input"
                >
                  <option value="1">Active</option>
                  <option value="0">Passive</option>
                </select>
              </Field>
              <Field label="Primary Color">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="h-10 w-14 bg-transparent border border-white/10"
                  />
                  <input
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="adm-input flex-1"
                  />
                </div>
              </Field>
              <Field label="Secondary Color">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={form.secondary_color}
                    onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                    className="h-10 w-14 bg-transparent border border-white/10"
                  />
                  <input
                    value={form.secondary_color}
                    onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                    className="adm-input flex-1"
                  />
                </div>
              </Field>
              <Field label="Logo">
                <div className="flex items-center gap-3">
                  {form.logo && <img src={form.logo} alt="" className="h-10 w-auto" />}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadFile("logo", e.target.files?.[0])}
                    className="text-xs text-zinc-400"
                    data-testid="brand-form-logo"
                  />
                </div>
              </Field>
              <Field label="Banner Image">
                <div className="flex items-center gap-3">
                  {form.banner_image && <img src={form.banner_image} alt="" className="h-10 w-auto" />}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadFile("banner_image", e.target.files?.[0])}
                    className="text-xs text-zinc-400"
                  />
                </div>
              </Field>
              <Field label="Description" full>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="adm-input"
                />
              </Field>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={close} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5">
                Cancel
              </button>
              <button type="submit" className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]" data-testid="brand-form-save">
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; }
        .adm-input:focus { border-color: rgba(255,255,255,0.3); }
      `}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
